import { memo } from 'react';
import PropTypes from 'prop-types';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ProtocolStepIcon from 'features/stepByStepProtocol/components/ProtocolStepIcon/ProtocolStepIcon';
import styles from './ProtocolCard.module.css';

/**
 * Card shared by Detected, the protocol sections and the unavailable state (Figma `16780:56249`):
 * icon, title, optional addon next to the title, a subtitle and a note line, and content. With `onToggle` the
 * header is the expand button and shows the chevron; the content is rendered only while expanded.
 */
function ProtocolCard({ icon, title, titleAddon = null, subtitle = '', note = '', onToggle, expanded = false, contentId, children = null }) {
    const isExpandable = typeof onToggle === 'function';
    const heading = (
        <>
            <ProtocolStepIcon icon={icon} />
            <span className={styles['card__heading']}>
                <span className={styles['card__title-row']}>
                    <span className={styles['card__title']}>{title}</span>
                    {titleAddon}
                </span>
                {subtitle && <span className={styles['card__subtitle']}>{subtitle}</span>}
                {note && <span className={styles['card__subtitle']}>{note}</span>}
            </span>
            {isExpandable && (
                <span className={styles['card__chevron']} aria-hidden="true">
                    {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </span>
            )}
        </>
    );

    return (
        <div className={styles['card']}>
            {isExpandable ? (
                <button
                    type="button"
                    className={styles['card__header']}
                    aria-expanded={expanded}
                    // The content exists only while expanded; point at it only then.
                    aria-controls={expanded ? contentId : undefined}
                    onClick={onToggle}
                >
                    {heading}
                </button>
            ) : (
                <div className={styles['card__header']}>{heading}</div>
            )}
            {(!isExpandable || expanded) && children && (
                <div id={contentId} className={styles['card__content']}>
                    {children}
                </div>
            )}
        </div>
    );
}

ProtocolCard.propTypes = {
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    titleAddon: PropTypes.node,
    subtitle: PropTypes.string,
    note: PropTypes.string,
    onToggle: PropTypes.func,
    expanded: PropTypes.bool,
    contentId: PropTypes.string,
    children: PropTypes.node,
};

export default memo(ProtocolCard);
